/**
 * Auth-readiness gate.
 *
 * On a hard page load, dozens of components fire authenticated requests on
 * mount — before the auth provider has finished establishing the session
 * (the HttpOnly-cookie handshake / `/api/auth/me` probe). Those early
 * requests either 401 (browser logs "Failed to load resource: 401") or
 * `TypeError: Failed to fetch`, flooding the console even though the page
 * self-recovers.
 *
 * This module exposes two independent "ready" signals — one for the regular
 * USER auth (AuthContext → authFetch/apiClient) and one for the TECH ADMIN
 * session (useTechAdminAuth → adminFetch). The fetch wrappers `await` the
 * matching signal before firing, so no authenticated request goes out until
 * the session probe has resolved (authenticated OR not). The provider marks
 * the signal ready in every completion path.
 *
 * Safety: each signal auto-resolves after a hard timeout so a missing/late
 * provider can never block requests indefinitely. In the test environment
 * the gates are inert (resolved immediately) so unit tests are unaffected.
 */

const IS_TEST =
  typeof process !== 'undefined' &&
  process.env &&
  process.env.NODE_ENV === 'test';

const SAFETY_TIMEOUT_MS = 8000;

function createGate() {
  let ready = false;
  let resolveFn = () => {};
  const promise = new Promise((resolve) => {
    resolveFn = resolve;
  });
  const mark = () => {
    if (!ready) {
      ready = true;
      resolveFn();
    }
  };
  // Never block forever — resolve after the safety window even if the
  // provider never calls mark() (e.g. a route mounted outside it).
  if (typeof window !== 'undefined' && !IS_TEST) {
    setTimeout(mark, SAFETY_TIMEOUT_MS);
  }
  return {
    mark,
    when: () => (ready || IS_TEST ? Promise.resolve() : promise),
    isReady: () => ready || IS_TEST,
  };
}

const userGate = createGate();
const adminGate = createGate();

export const markUserAuthReady = userGate.mark;
export const whenUserAuthReady = userGate.when;
export const isUserAuthReady = userGate.isReady;

export const markAdminAuthReady = adminGate.mark;
export const whenAdminAuthReady = adminGate.when;
export const isAdminAuthReady = adminGate.isReady;
